﻿import {$, $$, clickOnEnterKey, get, getDataJson, getDataText, postDataText, show} from "@/Utils"
import {handleSuccess, validateElement, validateForm} from "@/Validation"
import {initGoogleMap} from "@/StoreMap"
import {AddressInfoMinimal, ConfirmTradeIn, ReplaceProduct} from "@/interfaces"
import {DataAttributes, TradeInStep} from "@/enums"
import {initCarousels} from "@/sharedComponents/Carousel/Carousel"

const calcContainerId = "calcContainer";
let products: ReplaceProduct[] = [];
let product: ReplaceProduct;
let captcha: string;

function updateCalcHtml(html: string, calcContentElement: HTMLElement) {
    let ok = isOk(html, calcContentElement)
    if (!ok)
        return;
    if (calcContentElement) {
        const id = calcContentElement.id
        calcContentElement.parentElement.innerHTML = html;
        const calculatorHasId = id && id != "calc-";
        if (calculatorHasId) {
            // Select stale element again.
            calcContentElement = get(id)
            calcContentElement?.scrollIntoView({ behavior: "smooth", block: "end" }); // If canceled then there is nothing to scroll to.
        }
    } else {
        console.error(`Expected element with ID ${calcContainerId} is missing!`);
    }
}

function getClosestOrderProductId(target: HTMLElement) {
    return Number((target.closest("[data-orderproductid]") as HTMLDivElement)?.dataset.orderproductid)
}

// Checking if response has a message or plain html.
function hasMessage(obj: unknown): obj is { message?: string; error?: string } {
    if (!obj || typeof obj !== 'object') return false;
    const o = obj as any;
    return (typeof o.message === 'string' && o.message.length > 0)
        || (typeof o.error === 'string' && o.error.length > 0);
}

function isReplaceProductArray(arr: unknown): arr is ReplaceProduct[] {
    if (!Array.isArray(arr)) return false;
    // Shallow check a couple of fields we rely on when rendering
    return arr.every(p => p && typeof p === 'object' && 'id' in p && 'productName' in p);
}

function isOk(response: unknown, calcContentElement: HTMLElement) {
    if (typeof response === 'string') {
        try {
            response = JSON.parse(response);
        } catch {
            return true; // not JSON → likely HTML → OK
        }
    }

    if (hasMessage(response)) {
        const msg = (response.message || (response as any).error) as string;
        const container = calcContentElement.querySelector(".replace-product-search") || calcContentElement;
        if (container && msg) {
            const div = document.createElement("div");
            div.innerHTML = msg;
            container.appendChild(div);
        }
        return false;
    }

    return true;
}

function initSearch(calcContentElement) {
    const processResponse = (responseProducts: unknown, calcContentElement) => {
        let ok = isOk(responseProducts, calcContentElement)
        if (!ok) 
            return;
        if (!isReplaceProductArray(responseProducts)) {
            // Nothing to render
            return;
        }
        captcha = null;
        products = responseProducts;
        const formatProduct = (p: ReplaceProduct) =>
            `<li>
            <button type='button' class='autocomplete-list__result'
                data-productid="${p.id}">
                ${p.productName}
            </button/>
            </li>`;

        const autocompleteList = calcContentElement.querySelector(".autocomplete-list");
        autocompleteList.innerHTML = products
            .map(formatProduct)
            .reduce((acc, curr) => acc + curr, "");
    }

    const queryProducts = (searchTerm: string) => {
        let url = `${window.config.tradeIn.getProductsUrl}?searchTerm=${searchTerm}`;
        if (captcha){
            url = url + '&g-recaptcha-response='+ captcha
        }
        getDataJson(url, (responseProducts)  => processResponse(responseProducts, calcContentElement));
    }

    const clearResults = () => calcContentElement.querySelector(".autocomplete-list").innerHTML = "";

    const autocompleteClickListener = (e: Event) => {
        const target = e.target as HTMLElement;
        const pid = parseInt(target.getAttribute("data-productid"));
        if (isNaN(pid)) return;

        product = products.find(x => x.id === pid);
        product.relatedCartItem = getClosestOrderProductId(target);
        postDataText(window.config.tradeIn.evaluationUrl, product, (e) =>  updateCalcHtml(e, calcContentElement));
    }

    let timeout: number = null;
    const inputListener = (e: Event) => {
        window.clearTimeout(timeout);
        const input = e.currentTarget as HTMLInputElement;
        const searchTerm = input.value;

        if (searchTerm.length < 3) {
            return clearResults();
        }

        timeout = window.setTimeout(() => queryProducts(searchTerm), 750);
    }
    calcContentElement.querySelector(".searchProducts")?.addDataBoundEventListener("input", inputListener, "search-input");
    calcContentElement.querySelector(".autocomplete-list")?.addDataBoundEventListener("click", autocompleteClickListener, "autocomplete-list-click");
}

function initEvaluation(calcContentElement: HTMLElement) {
    const handler = (e: Event) => {
        const target = e.target as HTMLElement;
        const radio = target.previousElementSibling as HTMLInputElement;
        radio.checked = true;

        const container = target.closest("[data-question-container]");
        const nextContainer = container.nextElementSibling as HTMLElement;
        if (nextContainer) {
            nextContainer.classList.remove("disabled-question");
        } else {
            const nextButton = calcContentElement.querySelector(".trade-in-next") as HTMLElement;
            show(nextButton);
        }
    }

    calcContentElement.querySelectorAll(".replace-answers span").forEach(x => {
        x.addDataBoundEventListener("click", handler)
        x.addDataBoundEventListener("keydown", (e: KeyboardEvent) => {
            clickOnEnterKey(e, x as HTMLElement)
        }, "enter-keypress-bound")
    });
}

function initSummary(calcContentElement: HTMLElement) {
    window.addEventListener("AddressSelected", ((ev: Event) => {
        const { city = "", county = "", settlement = "" } = (ev as CustomEvent)?.detail ?? {};

        const manualCityInput = calcContentElement.querySelector<HTMLInputElement>(".manual-address-search-input");
        const countyInput = calcContentElement.querySelector<HTMLInputElement>("#AddressInfo_County");
        const cityInput = calcContentElement.querySelector<HTMLInputElement>("#AddressInfo_City");

        if (manualCityInput) {
            // LT Example case: Visaginas, Visagino sav.
            const [parsedCity, parsedCounty] = getCityAndCountyFromAddressSearch(manualCityInput, calcContentElement);
            // Apply parsed values with sensible fallbacks from the event payload
            manualCityInput.value = (parsedCity || city || settlement || "").trim();
            if (countyInput) countyInput.value = (parsedCounty || county || "").trim();
        } else {
            // LV example case: there may be no city but a settlement. E.g. Rīgas-Liepājas šoseja 97
            if (cityInput) cityInput.value = (city || settlement || "").trim();
            if (countyInput) countyInput.value = (county || "").trim();
        }

        openManualIfInvalidAfterSelection();
    }) as EventListener)
        
    const confirmBtn = calcContentElement.querySelector('.trade-in-next') as HTMLElement;
    if (!confirmBtn) return;

    // Generic helper to find a validation span for a given input by its name
    const getValidationSpanFor = (el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
        const name = (el as HTMLInputElement).name;
        if (!name) return null;
        return calcContentElement.querySelector<HTMLElement>(`[data-valmsg-for="${CSS.escape(name)}"]`);
    };

    const requiredInputs = Array.from(
        calcContentElement.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
            '#Product_Imei, #Product_BankAccountNumber, .trade-in-confirm-checkbox, .trade-in-confirm-locks, ' +
            '.trade-in-confirm-campaign-terms, .input-address_search, #AddressInfo_PostalCode, ' +
            '#AddressInfo_Address, #AddressInfo_City, #AddressInfo_County'
        )
    );

    if (requiredInputs.length === 0) return;

    const isInputValid = (el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
        // Special rule: address search field is optional if manual address AND postal code are filled
        if ((el as HTMLElement).classList?.contains('input-address_search')) {
            const manualAddress = calcContentElement.querySelector<HTMLInputElement>('#AddressInfo_Address');
            const manualPostal = calcContentElement.querySelector<HTMLInputElement>('#AddressInfo_PostalCode');
            const manualOk = !!manualAddress?.value?.trim() && !!manualPostal?.value?.trim();
            if (manualOk) return true;
            // else fall through to default validation for the search field itself
        }
        const type = (el as HTMLInputElement).type;
        if (type === 'checkbox') {
            return (el as HTMLInputElement).checked;
        }
        if (type === 'radio') {
            // For radios, ensure some radio with the same name is checked
            const name = (el as HTMLInputElement).name;
            const group = calcContentElement.querySelectorAll<HTMLInputElement>(`input[type="radio"][name="${CSS.escape(name)}"]`);
            return Array.from(group).some(r => r.checked);
        }
        // For text-like inputs, require non-empty value; honor pattern if provided
        if ('value' in el) {
            const value = (el as HTMLInputElement).value?.trim();
            if (!value) return false;
            const pattern = (el as HTMLInputElement).getAttribute('pattern');
            if (pattern) {
                try {
                    const re = new RegExp(`^${pattern}$`);
                    return re.test(value);
                } catch {
                    // If the pattern is invalid, fall back to non-empty
                    return true;
                }
            }
            return true;
        }
        return true;
    };
    const isEffectivelyOptional = (el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
        if (!(el as HTMLElement).classList?.contains('input-address_search')) {
            return false;
        }

        const addrEl = calcContentElement.querySelector<HTMLInputElement>('#AddressInfo_Address');
        const cityEl = calcContentElement.querySelector<HTMLInputElement>('#AddressInfo_City');
        const countyEl = calcContentElement.querySelector<HTMLInputElement>('#AddressInfo_County');
        const postalEl = calcContentElement.querySelector<HTMLInputElement>('#AddressInfo_PostalCode');

        // Start with values from normal manual fields
        let address = addrEl?.value?.trim();
        let city = cityEl?.value?.trim();
        let county = countyEl?.value?.trim();

        // If manual address search block is present, prefer its input
        const manualSearchContainer = calcContentElement.querySelector('#LTManualAddressSearch');
        if (manualSearchContainer) {
            const manualSearchInput = calcContentElement.querySelector<HTMLInputElement>('.manual-address-search-input');
            const [parsedCity, parsedCounty] = getCityAndCountyFromAddressSearch(manualSearchInput as HTMLInputElement, calcContentElement);

            // Populate city/county fields if they are empty
            if (!county && countyEl && parsedCounty) countyEl.value = parsedCounty;
            if (!city && cityEl && parsedCity) cityEl.value = parsedCity;

            // Use the visible manual search text as address if present
            address = manualSearchInput?.value?.trim() || address;

            // Re-read to keep local variables in sync with potential field updates
            city = cityEl?.value?.trim();
            county = countyEl?.value?.trim();
        }

        const postal = postalEl?.value?.trim();

        return !!address && !!postal && !!city && !!county;
    };
    
    const isTextLike = (el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
        const type = (el as HTMLInputElement).type;
        return type !== 'checkbox' && type !== 'radio' && 'value' in el;
    };

    const validateAndRender = (el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
        // Prefer shared validation for consistency across the site
        if (isEffectivelyOptional(el)) {
            // If effectively optional (e.g., address search when manual fields are filled), mark success and exit
            handleSuccess(el as unknown as HTMLElement);
            return;
        }
        validateElement(el as unknown as HTMLElement);
    };

    const bindTextValidation = (el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, baseKey: string) => {
        if (!isTextLike(el)) return;
        // Render errors only after focus leaves the field
        (el as HTMLElement).addDataBoundEventListener('blur', () => validateAndRender(el), `${baseKey}-blur`);

        //On input, only re-check whether the Next button can be shown,
        // but do NOT render errors here.
        (el as HTMLElement).addDataBoundEventListener('input', () => tryShowNextButton(), `${baseKey}-input`);
    };

    const tryShowNextButton = () => {
        const allOk = requiredInputs.every(el => {
            if (isEffectivelyOptional(el)) 
                return true;
            return isInputValid(el);
        });
        if (allOk) {
            show(confirmBtn);
            confirmBtn.classList.remove('button--disabled');
        } else {
            confirmBtn.classList.add('button--disabled');
        }
    };

    confirmBtn.classList.add('button--disabled');

    // Listen for changes on required inputs
    requiredInputs.forEach(inp => {
        const element = inp as HTMLInputElement;
        element.addDataBoundEventListener('change', tryShowNextButton, 'tradein-required-change');
        element.addDataBoundEventListener('input', tryShowNextButton, 'tradein-required-input');

        const baseKey = (element.id?.trim())
            || (element.name?.replace(/[^a-zA-Z0-9_-]/g, '-'))
            || (element.classList?.item(0) ?? 'tradein-field');

        bindTextValidation(element, `tradein-${baseKey}`);
    });

    const manualToggleAnchor = calcContentElement.querySelector<HTMLAnchorElement>('.address-manually');
    const manualContainer = calcContentElement.querySelector<HTMLElement>('.manual-address-container');
    
    const isManualHidden = () => !!manualContainer && manualContainer.classList.contains('manual-address__hidden');

    // Open manual only if the user selected an entry from the address search dropdown and the field is invalid afterwards
    const addressSearchInput = calcContentElement.querySelector<HTMLInputElement>('.input-address_search');

    const openManualIfInvalidAfterSelection = () => {
        if (!manualToggleAnchor || !manualContainer || !addressSearchInput) return;
        // Defer check to allow selection logic to populate input/validation
        setTimeout(() => {
            const span = getValidationSpanFor(addressSearchInput);
            const hasErrorClass = addressSearchInput.classList.contains('input-validation-error');
            const spanShowsError = !!span && span.classList.contains('field-validation-error');
            // Revalidate using shared validator unless field is effectively optional
            const validByShared = isEffectivelyOptional(addressSearchInput);
            const invalid = hasErrorClass || spanShowsError || !validByShared;
            if (invalid && isManualHidden()) {
                manualToggleAnchor.click();
            }
        }, 500);
    };

    // don't render errors on the first load to avoid red fields
    // Allow confirming only if everything is already valid by value, respecting optionality.
    const initialAllOk = requiredInputs.every(el => isEffectivelyOptional(el) || isInputValid(el));
    if (initialAllOk) {
        show(confirmBtn);
        confirmBtn.classList.remove('button--disabled');
    }
}

function getProduct(calcContentElement: HTMLElement) {
    const answers: number[] = [];
    const answerTexts: string[] = [];
    const questions: string[] = [];
    const questionLabels: string[] = [];

    calcContentElement.querySelectorAll("[data-question-container]").forEach(x => {
        const input = x.querySelector(".replace-answer:checked") as HTMLInputElement;
        answers.push(parseInt(input.value));

        const answerText = input.nextElementSibling as HTMLElement;
        answerTexts.push(answerText.textContent);

        const question = x.querySelector(".replace-question") as HTMLElement;
        questions.push(question.textContent);
        questionLabels.push(question.getAttribute("data-label"));
    });

    product.answers = answers;
    product.answerTexts = answerTexts;
    product.questions = questions;
    product.questionLabels = questionLabels;
}

function getCityAndCountyFromAddressSearch(manualCityInput: HTMLInputElement, calcContentElement: HTMLElement) {
    let city;
    let county;
    if (manualCityInput && (manualCityInput as HTMLInputElement).value.length > 3) {
        const manualCityValue = manualCityInput.value;
        const parts = manualCityValue.split(',').map(part => part.trim());
        if (parts.length > 1) {
            // Example value: Visaginas, Visagino sav.
            city = parts[0];
            county = parts.slice(1).join(', '); // Assign everything after the first comma to county
        } else {
            city = manualCityValue;
        }
    } else {
        city = (calcContentElement.querySelector("#AddressInfo_City") as HTMLInputElement).value;
    }
    return [city, county]
}

function initNav(step: TradeInStep, calcContentElement: HTMLElement) {
    if (step === TradeInStep.Search) return;

    const back = calcContentElement.querySelector(".trade-in-back") as HTMLElement;
    const next = calcContentElement.querySelector(".trade-in-next") as HTMLElement;
    const cancelBtn = calcContentElement.querySelector(".trade-in-cancel") as HTMLElement;
    let backClick: Function = null;

    if (step === TradeInStep.Evaluation) {
        let url = window.config.tradeIn.searchUrl;
        const relatedOrderProductId = getClosestOrderProductId(calcContentElement as HTMLElement)
        if (relatedOrderProductId){
            url = url + "?relatedOrderProductId=" + relatedOrderProductId;
        }
        backClick = () => getDataText(url, (e)  => updateCalcHtml(e, calcContentElement));
        const nextClick = () => {
            getProduct(calcContentElement);
            postDataText(window.config.tradeIn.summaryUrl, product, (e) => { updateCalcHtml(e, calcContentElement); });

        }
        next.addDataBoundEventListener("click", nextClick);
    }
    if (step === TradeInStep.Summary) {
   
        backClick = function (e) {
            // Refreshed summary and went back to evaluation.
            if (product == undefined) {
                product = {
                    id: 0,
                    productName: "",
                    relatedCartItem: getClosestOrderProductId(e.target as HTMLElement),
                    answers: [],
                    answerTexts: [],
                    questions: [],
                    questionLabels: []
                } as ReplaceProduct;
            }
            return postDataText(window.config.tradeIn.evaluationUrl, product, (e) => updateCalcHtml(e, calcContentElement));
        };
        const nextClick = (e) => {
            const form = calcContentElement.querySelector('.replace-product-form') as HTMLFormElement;
            if (!validateForm(form))
                return;
            const id = getClosestOrderProductId(e.target as HTMLElement);
            const bankAccountId = calcContentElement.querySelector("#Product_BankAccountNumber") as HTMLInputElement;
            const imei = calcContentElement.querySelector("#Product_Imei") as HTMLInputElement;

            let confirmModel: ConfirmTradeIn = {
                orderProductId: id,
                bankAccountNumber: bankAccountId.value,
                imei: imei.value,
            } as ConfirmTradeIn;
            confirmModel.addressInfo = {
                address: (calcContentElement.querySelector("#AddressInfo_Address") as HTMLInputElement).value,
                county: (calcContentElement.querySelector("#AddressInfo_County") as HTMLInputElement).value,
                postalCode: (calcContentElement.querySelector("#AddressInfo_PostalCode") as HTMLInputElement).value,
            } as AddressInfoMinimal
            const addressSearchField = calcContentElement.querySelector(".manual-address-search-input") as HTMLInputElement;
            [confirmModel.addressInfo.city, confirmModel.addressInfo.county] = getCityAndCountyFromAddressSearch(addressSearchField, calcContentElement);
            if (calcContentElement.querySelector("#AddressInfo_Settlement")){
                confirmModel.addressInfo.settlement = (calcContentElement.querySelector("#AddressInfo_Settlement" )as HTMLInputElement).value
            }
            postDataText(window.config.tradeIn.confirmUrl, confirmModel, (e) => updateCalcHtml(e, calcContentElement));
        }     
        const cancelClick = (e) => {
            const id = getClosestOrderProductId(e.target as HTMLElement);
            postDataText(window.config.tradeIn.cancelUrl, id, (e) => updateCalcHtml(e, calcContentElement));
        }  
        
        if (cancelBtn) {
            cancelBtn?.addDataBoundEventListener("click", cancelClick);
        } else {
            next?.addDataBoundEventListener("click", nextClick);
        }
    }

    back?.addDataBoundEventListener("click", backClick);
}

function init() {
    const calcContentElement = $$(".calc-content");
    calcContentElement.forEach(calcContentElement => {
            const step = parseInt(calcContentElement.getAttribute(DataAttributes.Step)) as TradeInStep;
            switch (step) {
                case TradeInStep.Search:
                    initSearch(calcContentElement);
                    break;
                case TradeInStep.Evaluation:
                    initEvaluation(calcContentElement);
                    break;
                case TradeInStep.Summary:
                    initSummary(calcContentElement);
                    break;
            }

            initNav(step, calcContentElement)
            calcContentElement.addDataBoundEventListener('captchaSuccess', (e) => {
                captcha=e.detail
                const element = calcContentElement.querySelector(".replace-product-search.d-none");
                element?.classList.remove("d-none");
            }, "captcha-listener");
        }
    );

}

function initTradeIn() {

    initGoogleMap();

    const mapContainerId = "mapContainer"
    const map = get(mapContainerId);
    if (map) {
        if (!map.querySelector(".map-content")) {
            map.innerHTML = "";
            map.append($(".map-content"));
        }
    } else {
        console.error(`Expected element with ID ${mapContainerId} is missing!`);
    }
    // Only one calculator per page.
    const calc = get(calcContainerId);
    if (calc) {
        if (!calc.querySelector(".calc-content")) {
            calc.innerHTML = "";
            calc.append($(".calc-content"));
        }
    } else {
        console.error(`Expected element with ID ${calcContainerId} is missing!`);
    }
    // This supports multiple trade ins per page.
    init();
}



function initializeTradingAndCarouselComponents() {
    initTradeIn();
    initCarousels();
}

window.addEventListener("afterLoadDone", () => {
    // When user removes item from the cart, we need to rebind search input.
    initializeTradingAndCarouselComponents();
})
window.addEventListener("load", () => {
    initializeTradingAndCarouselComponents();
});
